<script setup lang="ts">
import { computed } from 'vue';

/**
 * Compact count badge for sidebar / nav items. Hides when count is 0 or
 * undefined (no "0" badges in the nav per the followup spec). Optional
 * red dot overlays the badge to signal urgency (past-due invoice).
 *
 * Variants map to tenant tokens so per-tenant theming applies without
 * special casing here:
 *   - primary: tenant primary (default; used for the invoice badge)
 *   - pending: amber (used for the quotes badge when wired up)
 *   - info:    neutral / muted (used if a non-urgent counter is desired)
 */
const props = withDefaults(
  defineProps<{
    count?: number | null;
    variant?: 'primary' | 'pending' | 'info';
    dot?: boolean;
    tooltip?: string;
    /** When true, the parent nav row is the current route. The info
     *  variant deepens its chip + text one slate step so it reads
     *  solidly against the row's primary-tinted background. */
    active?: boolean;
  }>(),
  {
    count: 0,
    variant: 'primary',
    dot: false,
    tooltip: undefined,
    active: false,
  },
);

const shouldRender = computed(
  () => typeof props.count === 'number' && props.count > 0,
);

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'pending':
      return 'bg-amber-500 text-white';
    case 'info':
      // Filled slate chip — enough visual weight to register as an
      // intentional counter, soft enough not to compete with the
      // saturated primary/pending pills. Explicit slate (not tenant
      // primary) keeps the chip readable on every tenant scheme.
      // When the row is active, step one slate level darker so the chip
      // still reads against the primary-tinted row bg.
      return props.active
        ? 'bg-slate-300 text-slate-800 dark:bg-slate-600 dark:text-slate-100'
        : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
    case 'primary':
    default:
      // White-on-tenant-primary in light; black-on-lightened-primary in
      // dark (dark mode applies oklch lightness floor max(0.72, l), so
      // dark text is needed for AA contrast). Explicit white/black rather
      // than the `text-primary-text` CSS var so the cascade is obvious and
      // matches the dark mode strategy bound to html.dark via main.css's
      // @custom-variant.
      return 'bg-primary text-white dark:text-black';
  }
});
</script>

<template>
  <span
    v-if="shouldRender"
    class="relative inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold leading-none whitespace-nowrap"
    :class="variantClasses"
    :title="tooltip"
    :aria-label="tooltip ?? String(count ?? 0)"
    data-testid="nav-badge"
  >
    {{ count }}
    <span
      v-if="dot"
      class="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-red-500 border border-canvas"
      aria-hidden="true"
      data-testid="nav-badge-dot"
    />
  </span>
</template>
